
import ConfigService from "./ConfigService";
import HttpService from "./HttpService";
import AuthService from "./AuthService";
import JavascriptHelpers from "../components/lib/Javascript/JavascriptHelpers";
import IdentityService from "./IdentityService";

import _ from "lodash";

import ObsSearchParams from "../models/ObsSearchParams";
import { formatISO, parseISO, format } from "date-fns";
import JwtToken from "../components/lib/tokens/JwtToken";
import { AppSecurityKey } from "../models/AppSecurityKey";
import RoleManager from "../models/RoleManager";


export default class CoreService 
{
  constructor(coreDomain) 
  {
    this.configService = new ConfigService();
    this.domain = coreDomain || this.configService.getCoreServiceHost();
    this.authService = new AuthService();
  }

  async fetchVersion(token) 
  {
    const path = "/api/admin/version";
    var response = await HttpService.get(this.domain, path, token);
    return response;
  }

  async pingCore(token) 
  {
    const path = "/api/admin/ping";
    var response = await HttpService.get(this.domain, path, token);
    return response;
  }

  async pingCoreDb(token)
  {
    const path = "/api/observation/coredbping";
    var response = await HttpService.get(this.domain, path, token);
    return response;
  }

  async fetchOrgConfiguration(gcid, token)
  {
    const path = "/api/orgconfig/gcid/" + gcid + "/orgconfig";
    var response = await HttpService.get(this.domain, path, token);
    return response;
  }

  async fetchIdNameMetaDataList(gcid, token)
  {
    const path = "/api/datalist/gcid/" + gcid + "/datalistmetadata?storageschema=IdNameData";
    var response = await HttpService.get(this.domain, path, token);
    return response;
  }

  async fetchLocations(gcid, token)
  {
    var result = await this.fetchIdNameDataList("inspectionLocations", gcid, token);
    return result;
  }

  async fetchIdNameDataList(dataListName, gcid, token)
  {
    const path = "/api/datalist/gcid/" + gcid + "/idnamedatalist/" + dataListName;
    var response = await HttpService.get(this.domain, path, token);
    return response;
  }

  async postIdNameDataList(dataList, gcid, token)
  {
    const path = "/api/datalist/gcid/" + gcid + "/idnamedatalist";
    var jsonObj = JSON.stringify(dataList);
    var response = await HttpService.postJson(this.domain, path, token, jsonObj);
    return response;
  }

  async postIdNameDataListDeleteRequest(listName, gcid, token)
  {
    const path = "/api/datalist/gcid/" + gcid + "/datalistdeleterequest/" + listName;
    var response = await HttpService.postJson(this.domain, path, token, "{}");
    return response;
  }



  async fetchSchema(schemaId, gcid, token)
  {
    const path = "/api/schema/gcid/" + gcid + "/schema/" + schemaId;
    var response = await HttpService.get(this.domain, path, token);
    return response;
  }

  async fetchGridSchema(schemaId, gcid, token)
  {
    const path = "/api/observations/dashboard/gcid/" + gcid + "/schema/" + schemaId;
    var response = await HttpService.get(this.domain, path, token);
    return response;
  }


  async fetchSchemaListByGcid(gcid, token)
  {
    const path = "/api/schema/gcid/" + gcid + "/schema/metadata";
    var response = await HttpService.get(this.domain, path, token);
    return response;
  }

  async fetchObservationList(searchParams, schemaSearch, gcid, token)
  {
    // convert internal format of queryString to that used by API
    // there is a special case with "ALL" for locations (and maybe observers, but start with locations)
    var newSearchParams = _.cloneDeep(searchParams);
    if ((searchParams.location) && (searchParams.location.toLowerCase() === "all"))
    {
      // test if this user has a global.search role
      if (!RoleManager.hasRoleRights(AppSecurityKey.SEARCH_ALL, token))
      {
        // we need to fetch ONLY the locations this user has rights to
        var tokenPayload = JwtToken.getTokenPayload(token);
        newSearchParams.location = tokenPayload.locations; // this will expand it to an array of locations user has rights to.
        // the toApiSearchObject should expand this to conver to MultiValueParams.
      }
    }

    var searchParamsObj = ObsSearchParams.toApiSearchObject(newSearchParams, schemaSearch);
    const path = "/api/observation/gcid/" + gcid + "/observation/search";
    var obj = JSON.stringify(searchParamsObj);
    var response = await HttpService.postJson(this.domain, path, token, obj);
    return response;
  }

  async fetchObservation(observationId, gcid, token)
  {
    const path = "/api/observation/gcid/" + gcid + "/observation?id=" + observationId + "&gcid=" + gcid;
    var response = await HttpService.get(this.domain, path, token);
    return response;
  }


  async postObservation(obs, gcid, token)
  {
    var hasImages = (obs.data?.Raw?.userimages) ? (obs.data.Raw.userimages.length > 0) : false;
    var hasDocs = (obs.data?.Raw?.userdocs) ? (obs.data.Raw.userdocs.length > 0) : false;
    obs.data.hasImages = hasImages;
    obs.data.hasDocs = hasDocs;
    var snapshot = JSON.stringify(obs); // note that the hash refers ONLY to the JsonSnapshot of the observation, not the entire object here.
    var obsHash = JavascriptHelpers.hashString(snapshot);
    obs.identifiers.status = "posted";
    obs.identifiers.statusReason = 'posted to core';
    var postObservation = {
      Id: obs.identifiers.id  ?? IdentityService.makeUuid(),
      Gcid: gcid,
      ObservedBy: obs.data.UnderInspection.Inspector,
      ObservationLocation: (obs.data.UnderInspection.Location) ? obs.data.UnderInspection.Location: "Unknown",
      ObservationDate: obs.data.UnderInspection.InspectionDate,
      ObservationType: "QADOCKINSPECTION",
      RecordedDate: obs.data.UnderInspection.InspectionDate,
      SchemaId: obs.data.schema.id,
      SchemaVersion: obs.data.schema.schemaVersion,
      ObservationHash: obsHash.toString(),
      ScoreReason: obs.score.Score.ScoreReason,
      ScoreGrade: (obs.score.Score.Grade.Weighted) ? obs.score.Score.Grade.Weighted: obs.score.Score.Grade.Raw,
      ScoreGradeInt: 0,
      HasImages: hasImages,
      HasDocs: hasDocs,
      Status: "Post",
      StatusReason: "Post from Local Queue",
      ObservationSnapshot: JSON.stringify(obs),
    };
    const path = "/api/observation/qaobservationpost";
    console.log("About to post to core from CoreService:  " + path);
    var response = await HttpService.postJson(this.domain, path, token, postObservation);
    console.log("response: ");
    console.info(response);
    return response;
  }


  async postObservationUpdate(updateEvent, obs, gcid, token)
  {
    var hasImages = (obs.data?.Raw?.userimages) ? (obs.data.Raw.userimages.length > 0) : false;
    var hasDocs = (obs.data?.Raw?.userdocs) ? (obs.data.Raw.userdocs.length > 0) : false;
    obs.data.hasImages = hasImages;
    obs.data.hasDocs = hasDocs;
    var snapshot = JSON.stringify(obs); // note that the hash refers ONLY to the JsonSnapshot of the observation, not the entire object here.
    var obsHash = JavascriptHelpers.hashString(snapshot);
    // CONVERT into postable inspection observation object
    var postObservation = {
      Id: obs.identifiers.id  ?? IdentityService.makeUuid(),
      Gcid: gcid,
      ObservedBy: obs.data.UnderInspection.Inspector,
      ObservationLocation: (obs.data.UnderInspection.Location) ? obs.data.UnderInspection.Location: "Unknown",
      ObservationDate: obs.data.UnderInspection.InspectionDate,
      ObservationType: "QADOCKINSPECTION",
      RecordedDate: obs.data.UnderInspection.InspectionDate,
      SchemaId: obs.data.schema.id,
      SchemaVersion: obs.data.schema.schemaVersion,
      ObservationHash: obsHash.toString(),
      ScoreReason: obs.score.Score.ScoreReason,
      ScoreGrade: (obs.score.Score.Grade.Weighted) ? obs.score.Score.Grade.Weighted: obs.score.Score.Grade.Raw,
      ScoreGradeInt: 0,
      HasImages: hasImages,
      HasDocs: hasDocs,
      Status: "Post",
      StatusReason: "Post from Local Queue",
      ObservationSnapshot: JSON.stringify(obs),
    };
    updateEvent.eventSnapshot = JSON.stringify(updateEvent);
    var postData = { Observation: postObservation, Event: updateEvent };
    const path = "/api/observation/qaobservationupdate";
    var response = await HttpService.postJson(this.domain, path, token, postData);
    return response;
}


  async postObservationVoid(voidEvent, gcid, token)
  {
    const path = "/api/observation/qaobservationvoid";
    voidEvent.eventSnapshot = JSON.stringify(voidEvent);
    var response = await HttpService.postJson(this.domain, path, token, voidEvent);
    return response;
  }





  async fetchErrorLogList(eventType, iso8601StartDateTime, gcid, token)
  {
    const path = "/api/errorlog/gcid/" + gcid + "/errorevent?maxCount=20&startDate=" + iso8601StartDateTime + "&eventtype=" + eventType;
    var response = await HttpService.get(this.domain, path, token);
    return response;
  }


  async fetchBatchStatusByHour(daysOld, gcid, token)
  {
    const path = "/api/dashboard/gcid/" + gcid + "/postresultbyhour/daysold/" + daysOld + "?status=all";
    var response = await HttpService.get(this.domain, path, token);
    return response;
  }

  async fetchBatchStatusBySearch(searchParams, schemaSearch, gcid, token)
  {
    var searchParamsObj = ObsSearchParams.toApiSearchObject(searchParams, schemaSearch);
    var obj = JSON.stringify(searchParamsObj);
    const path = "/api/dashboard/gcid/" + gcid + "/postresultbyhour/search/";
    var response = await HttpService.postJson(this.domain, path, token, obj);
    return response;
  }

  async fetchToDateLocationData(gcid, token)
  {
    const path = '/api/dashboard/gcid/' + gcid + '/location/todatecount';
    var response = await HttpService.get(this.domain, path, token);
    return response;
  }

  async fetchCountPctByPropResult(countProp, searchParams, schemaSearch, gcid, token)
  {
    var searchParamsObj = ObsSearchParams.toApiSearchObject(searchParams, schemaSearch);
    var postObj = {"CountProp": countProp, "SearchParams": searchParamsObj};
    const path = "/api/dashboard/gcid/" + gcid + "/countpctbyprop/search";
    var response = await HttpService.postJson(this.domain, path, token, postObj);
    return response;
  }


  async fetchCountPctPartitionByPropResult(partitionProp, countProp, searchParams, schemaSearch, gcid, token)
  {
    var searchParamsObj = ObsSearchParams.toApiSearchObject(searchParams, schemaSearch);
    var postObj = JSON.stringify({"PartitionProp": partitionProp, "CountProp": countProp, "SearchParams": searchParamsObj});
    const path = "/api/dashboard/gcid/" + gcid + "/countpctpartitionbyprop/search";
    var response = await HttpService.postJson(this.domain, path, token, postObj);
    return response;
  }



  /* region OBSOLETE CHART SERVICES */
  
  async fetchObsGradePieList(searchParams, schemaSearch, gcid, token) 
  {
    var searchParamsObj = ObsSearchParams.toApiSearchObject(searchParams, schemaSearch);
    var obj = JSON.stringify(searchParamsObj);
    const path = '/api/dashboard/gcid/' + gcid + '/countbygrade';
    var response = await HttpService.postJson(this.domain, path, token, obj);
    return response;
  }
  
  
  async postObsRankListRequest(searchParams, schemaSearch, rankSchema, gcid, token) 
  {
    var searchParamsObj = ObsSearchParams.toApiSearchObject(searchParams, schemaSearch);
    var compositeObj = {searchParams: searchParamsObj, rankSchema: rankSchema};
    var postObj = JSON.stringify(compositeObj);
    const path = '/api/dashboard/gcid/' + gcid + '/rankrequest';
    var response = await HttpService.postJson(this.domain, path, token, postObj);
    return response;
  }

  async fetchObsGrowerGradeList(searchParams, gcid, token)
  {
    var searchParamsObj = ObsSearchParams.toApiSearchObject(searchParams, schemaSearch);
    var obj = JSON.stringify(searchParamsObj);
    const path = '/api/observations/dashboard/gcid/' + gcid + '/countbygrowergrade';
    var response = await HttpService.postJson(this.domain, path, token,obj);
    return response;
  }

  /* endregion OBSOLETE CHART SERVICES */









  /*
  async fetchLog(eventType, hoursBeforeNow, token) {
    var iso8601StartDate = DateTime.now().setZone('America/Los_Angeles').minus({ hours: hoursBeforeNow }).toISO();
    const path = "/api/telemetry/gcid/cg/pollingtelemetryevent?maxCount=1000&startDate=" + iso8601StartDate + "&eventtype=" + eventType;
    var response = await HttpService.get(this.domain, path, token);
    return response;
  }
  */

  async fetchUserImgSasUri(imgId, ext, gcid, token) 
  {
    const path = "/api/storage/gcid/" + gcid + "/upload/uri/userimage/id/" + imgId + "/ext/" + ext;
    let sasUriResponse = await HttpService.get(this.domain, path, token);
    return sasUriResponse;
  }

  async fetchUserDocSasUri(docId, ext, gcid, token) 
  {
    const path = "/api/storage/gcid/" + gcid + "/upload/uri/userdoc/id/" + docId + "/ext/" + ext;
    let sasUriResponse = await HttpService.get(this.domain, path, token);
    return sasUriResponse;
  }

  async fetchUserRptSasUri(rptId, ext, gcid, token) 
  {
    const path = "/api/storage/gcid/" + gcid + "/upload/uri/userrpt/id/" + rptId + "/ext/" + ext;
    let sasUriResponse = await HttpService.get(this.domain, path, token);
    return sasUriResponse;
  }





  async fetchLookupEntityTypes(gcid, token)
  {
    const path = "/api/lookupentity/gcid/" + gcid + "/list/lookupentitytype"
    let response = await HttpService.get(this.domain, path, token);
    return response;
  }


  addLookupEntityIdentifiers(entityList, entityCardSchemas)
  {
    var data = [];
    // generate an additional property for EACH item in the dataResult
    for(var i=0; i<entityList.length; i++)
    {
      var schemaId = entityList[i].metaData.schemaId;
      var entity = this.addLookupEntityIdentifier(entityList[i], entityCardSchemas[schemaId]);
      data.push(entity);
    }
    return data;
  }


  addLookupEntityIdentifier(entity, entityCardSchema)
  {
    if (entityCardSchema.allowMultipleKeys)
    {
      var identifiers = entityCardSchema.identifiers.identifierFields.map((iFld) => 
      {
        let lbl = (entityCardSchema.identifiers.showFieldLabel) ? iFld.fieldLabel : "";
        let sep = (entityCardSchema.identifiers.fieldSeparator) ? entityCardSchema.identifiers.fieldSeparator : "";
        let val = entity.data.find((df) => df.id === iFld.fieldId)?.value ?? "";
        // we have some *special" fieldTypes which require a different approach
        // This should be broken out into a "factory" pattern, but I'm not sure where we are going with this, 
        // so for now I'm putting it here.
        if ((iFld.fieldType) && (iFld.fieldType === "datetime"))
        {
            if (val === "") {val = formatISO(new Date())}
            val = format(parseISO(val), iFld.display.format);
        } 
        if  ((iFld.fieldType) && (iFld.fieldType === "multientrykeypair"))
        {
          if (val === "")  val = [{key: "", val: ""}];
          if (!Array.isArray(val))
          {
            val = [{key: val, val: ""}];
          }
          var pairs = (iFld.display.showOnlyFirstPair) ? val[0] : val;
          var pairsVal = pairs.map((p) => {
            var pvKeys = Object.keys(p);
            let pvKey = (iFld.display.showKey) ? p[pvKeys[0]] : "";
            let pvVal = (iFld.display.showValue) ? p[pvKeys[1]] : "";
            let sep = (iFld.display.separator) ? iFld.display.separator : "";
            return pvKey + sep + pvVal;
          });
          val = pairsVal.join(sep);
        }
        return lbl + sep + val;
      });
      var fieldSeparator = (entityCardSchema.identifiers.fieldSeparator) ? entityCardSchema.identifiers.fieldSeparator : " ";
      var identifier = identifiers.join(fieldSeparator);
      entity.metaData.identifier = identifier;
    }
    else {
      entity.metaData.identifier = entity.data.id;
    }
    return entity;
  }


  async fetchLookupEntityByType(entityType, entityCardSchemas, gcid, token)
  {
    const path = "/api/lookupentity/gcid/bfresh/lookupentity/entitytype/" + entityType;
    let response = await HttpService.get(this.domain, path, token);
    // we need to do some translating here
    if (response.hasError) return response;
    var data = this.addLookupEntityIdentifiers(response.dataResult, entityCardSchemas);
    response.dataResult = data;
    return response;
  }

  async fetchLookupEntityByKey(entityType, schemaId, entityKey, entityCardSchema, gcid, token)
  {
    const path = "/api/lookupentity/gcid/" + gcid + "/lookupentity/entitytype/" + entityType + "/schema/" + schemaId + "/entitykey/" + entityKey;
    let response = await HttpService.get(this.domain, path, token);
    if (response.hasError) return response;
    // expects an dictionary
    var entityCardSchemas = {};
    entityCardSchemas[schemaId] =entityCardSchema;
    var data = this.addLookupEntityIdentifiers(response.dataResult, entityCardSchemas);
    response.dataResult = data;
    return response;
  }

  async fetchLookupEntityById(entityType, entityCardSchema, id, gcid, token)
  {
    const path = "/api/lookupentity/gcid/" + gcid + "/lookupentity/entitytype/" + entityType + "/id/" + id;
    let response = await HttpService.get(this.domain, path, token);
    if (response.hasError) return response;
    var entity = this.addLookupEntityIdentifier(response.dataResult, entityCardSchema);
    response.dataResult = entity;
    return response;
  }

  async postLookupEntity(entity, gcid, token)
  {
    const path = "/api/lookupentity/gcid/" + gcid + "/lookupentity";
    var postObj = JSON.stringify(entity);
    let response = await HttpService.postJson(this.domain, path, token, postObj);
    return response;
  }

  async deleteLookupEntity(entityKeyId, gcid, token)
  {
    const path = "/api/lookupentity/gcid/" + gcid + "/lookupnentitydeleterequest/id/" + entityKeyId;
    var postObj = JSON.stringify(entityKeyId);
    let response = await HttpService.postJson(this.domain, path, token, postObj);
    return response;
  }

  async deleteLookupEntityByEntityKeyValue(entityType, entityKeyValue, gcid, token)
  {
    const path = "/api/lookupentity/gcid/" + gcid + "/lookupentitydeleterequest/entitytype/" + entityType + "/entitykey/" + entityKeyValue;
    var postObj = JSON.stringify({entityType: entityType, entityKeyValue: entityKeyValue});
    let response = await HttpService.postJson(this.domain, path, token, postObj);
    return response;
  }




  // Subscriptions

    async fetchSubscriptionOrgSchema(subscriptionId, gcid, token)
    {
      const path = "/api/subscriber/gcid/" + gcid + "/subscriptionorgschema/subscriptionid/" + subscriptionId;
      let response = await HttpService.get(this.domain, path, token);
      if (response.hasError)
      {
        return response;
      }
      // let's go ahead and parse the schemas
      var parsedDataResult = JSON.parse(response.dataResult.snapshot);
      response.dataResult = parsedDataResult;
      return response;
    }


    async fetchSubscribersByForeignPropValue(subscriptionId, propName, propValue, gcid, token)
    {
      const path = "/api/subscriber/gcid/" + gcid + "/subscriptionsubscriberschema/subscriptionid/" + subscriptionId + "/propname/" + propName + "/propval/" + propValue;
      let response = await HttpService.get(this.domain, path, token);
      return response;
    }


    async fetchSubscriberList(gcid, token, ignoreActiveFlag = true)
    {
      var ignoreActiveFlagQueryString = (ignoreActiveFlag) ? "?ignoreactiveflag=true" : "?ignoreactiveflag=false";
      const path = "/api/subscriber/gcid/" + gcid + "/subscriptionsubscriberschema" + ignoreActiveFlagQueryString;
      let response = await HttpService.get(this.domain, path, token);
      if (response.hasError)
      {
        return response;
      }
      // let's go ahead and parse the schemas
      var parsedDataResult = response.dataResult.map((d) => JSON.parse(d.snapshot));
      response.dataResult = parsedDataResult;
      return response;
    }


    async postSubscriber(subscriber, subscriptionId, foreignPropId, gcid, token)
    {
      const path = "/api/subscriber/gcid/" + gcid + "/subscriptionsubscriberschema";
      subscriber.snapshot = JSON.stringify(subscriber);
      //var postObj = JSON.stringify(subscriber);
      var response = await HttpService.postJson(this.domain, path, token, subscriber);
      if (response.hasError) return response;
      // We are not using the foreignProp until we need it for performance reasons.
      // now we have to post the foreignProp
      /*
      var subscriberSubscription = subscriber.subscriptions.find((s) => s.name.toLowerCase() === subscriptionId.toLowerCase());
      if (foreignPropId)
      {
        var foreignPropNode = subscriberSubscription.selections.find((s) => s.name.toLowerCase() === foreignPropId.toLowerCase());
        var foreignPropValues = (foreignPropNode) ? foreignPropNode.values : ["ALL"];
        var foreignPropValuesResponse = await this.postForeignPropValues(subscriber.name, subscriptionId, foreignPropId, foreignPropValues, gcid, token);       
        if (foreignPropValuesResponse.hasError)
        {
          response.messages.push("There was a problem posting the Foreign Prop Values for subscriber " + subscriber.subscriberName);
          foreignPropValuesResponse.messages.forEach((m) => {
            response.messages.push(m);
          })
        }
      }
      */
      return response;
    }

    // foreignPropValues should be an array of values in the BODY of the post
    async postForeignPropValues(subscriberId, subscriptionId, foreignPropId, foreignPropValues, gcid, token)
    {
      const path = "/api/subscriber/gcid/" + gcid + "/subscriptionsubscriberschema/subscriptionid/" + subscriptionId + "/subscriberid/" + subscriberId + "/propname/" + foreignPropId;
      var postObj = JSON.stringify(foreignPropValues);
      var response = await HttpService.postJson(this.domain, path, token, postObj);
      return response;
    }



    async postShortUrlQuery(shortUrlKey, queryString, gcid, token)
    {
      const path = "/api/subscriber/gcid/" + gcid + "/shorturl";
      var shortUrl = {id: shortUrlKey, gcid: gcid, eventDate: formatISO(Date.now()), queryString: queryString};
      var postObj = JSON.stringify(shortUrl);
      var response = await HttpService.postJson(this.domain, path, token, postObj);
      return response;
    }

    async fetchShortUrl(shortUrlKey)
    {
      const path = "/api/subscriber/shorturl/id/" + shortUrlKey ;
      let response = await HttpService.get(this.domain, path); // note that there is NO token
      return response;
    }


    //note that this is the subscriber id - the guid - not the subscriber name
    async removeSubscriber(id, gcid, token)
    {
      const path = "/api/subscriber/gcid/" + gcid + "/subscriptionsubscriberschemadeleterequest/id/" + id;
      var postObj = "{}";
      var response = await HttpService.postJson(this.domain, path, token, postObj);
      return response;
    }


  // end Subscriptions



  // begin DataAPI

  async postObservationFactBatch(batch, gcid, token)
  {
    const path = "/api/dataapi/gcid/" + gcid + "/qaobservationfactbatchpost";
    var response = await HttpService.postJson(this.domain, path, token, batch);
    return response;
  }


  async postObservationFactChildBatch(batch, gcid, token)
  {
    const path = "/api/dataapi/gcid/" + gcid + "/qaobservationfactchildbatchpost";
    var response = await HttpService.postJson(this.domain, path, token, batch);
    return response;
  }


  async postObservationDimensionBatch(batch, gcid, token)
  {
    const path = "/api/dataapi/gcid/" + gcid + "/qaobservationdimensionbatchpost";
    var response = await HttpService.postJson(this.domain, path, token, batch);
    return response;
  }

  async fetchEndpoint(urlWithDomain, token)
  {
    let response = await HttpService.getByFullUrl(urlWithDomain, token);
    return response;
  }


  // end DataAPI


  // begin Proxy API

  async fetchApiProxyUrl(host, endpointPath, gcid, token)
  {
    let response = await HttpService.get(host, endpointPath, token);
    return response;
  }

  // end Proxy API


  // begin Inbound Queue

  async fetchInboundQueueOpenLines(location, gcid, token)
  {
    const path = "/api/inboundqueue/gcid/" + gcid + "/receiptline/open?location=" + location;
    let response = await HttpService.get(this.domain, path, token);
    return response;
  }

  async postInboundQueueReceiptLineByReceiptIdentifierLineIdentifier(receiptIdentifier, lineIdentifier, gcid, token)
  {
    const path = "/api/inboundqueue/gcid/" + gcid + "/receiptlinedeleterequest/receiptidentifier/" + receiptIdentifier + "/lineidentifier/" + lineIdentifier;
      var postObj = {receiptIdentifier: receiptIdentifier, lineIdentifier: lineIdentifier};
    let response = await HttpService.postJson(this.domain, path, token, postObj);
  }


  async fetchInboundQueueDetailKeys(schemaId, headerIdentifier, lineIdentifier, gcid, token)
  {
    const path = "/api/inboundqueue/gcid/" + gcid + "/detailkey/schemaid/" + schemaId + "/headeridentifier/" + headerIdentifier + "/lineidentifier/" + lineIdentifier;
    let response = await HttpService.get(this.domain, path, token);
    return response;
  }

  
  async fetchInboundQueueReceiptLineByKeyIdentifier(keyIdentifier, schemaId, gcid, token)
  {
    const path = "/api/inboundqueue/gcid/" + gcid + "/receiptline/schemaid/" + schemaId + "/keyIdentifier/" + keyIdentifier;
    let response = await HttpService.get(this.domain, path, token);
    return response;
  }

  async postInboundQueueDetailKeysDeleteRequest(keyIdentifierList, schemaId, gcid, token)
  {
    const path = "/api/inboundqueue/gcid/" + gcid + "/detailkeysdeleterequest/schemaid/" + schemaId;
    var postObj = keyIdentifierList;
    let response = await HttpService.postJson(this.domain, path, token, postObj);
    return response;
  }

  async fetchInboundQueueLineCountByLocation(gcid, token)
  {
    const path = "/api/inboundqueue/gcid/" + gcid + "/linecountbylocation";
    let response = await HttpService.get(this.domain, path, token);
    return response;
  }

/*
  async postInboundQueueReceiptDelete(receiptId, gcid, token)
  {
    const path = "/api/inboundqueue/gcid/" + gcid + "/receiptdeleterequest/id/" + receiptId;
    var response = await HttpService.postJson(this.domain, path, token, receiptId);
    return response;
  }

  async postInboundQueueReceiptLinesDelete(keysToDelete, schemaId, gcid, token)
  {
    const path = "/api/inboundqueue/gcid/" + gcid + "receiptlinesdeleterequest/schemaid/" + schemaId;
    var response = await HttpService.postJson(this.domain, path, token, keysToDelete);
    return response;
  }

  async postInboundQueueReceiptLineDelete(lineId, gcid, token)
  {
    const path = "/api/inboundqueue/gcid/" + gcid + "receiptlindeleterequest/id/" + lineId;
    var response = await HttpService.postJson(this.domain, path, token, lineId);
    return response;
  }

  */

  // end Inbound Queue


  // Error Events

  async postErrorEvent(errorEvent, gcid, token)
  {
    const path = "/api/errorlog/gcid/" + gcid + "/erroreventpostrequest";
    var postObj = errorEvent;
    let response = await HttpService.postJson(this.domain, path, token, postObj);
    return response;
  }

  // end Error Events
  
}